/**
 * useAddressManager.js
 * Handles address geocoding and marker management
 */

import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useMainStore } from '@/stores/main'
import { useBackendStore } from '@/stores/backend'
import { useDialogsStore } from '@/stores/dialogs'

const addressMarkerLabel = ''

export function useAddressManager(map, route) {
  const router = useRouter()
  const mainStore = useMainStore()
  const backendStore = useBackendStore()
  const dialogsStore = useDialogsStore()
  const addressMarker = ref(null)

  /**
   * Geocode an address using backend service
   * @param {string} address - Address to geocode
   * @returns {Promise<Object>} - Resolves with { lat, lng }
   */
  const geocodeAddress = async (address) => {
    console.log('geocodeAddress called with:', address)
    console.log('Dispatching backend/geocodeAddress...')

    try {
      const { latLng, accuracyType, formattedAddress } = await backendStore.geocodeAddress(address)
      console.log('backend/geocodeAddress response:', { latLng, accuracyType, formattedAddress })

      // Show upgrade dialog if not rooftop accuracy
      if (accuracyType !== 'rooftop') {
        dialogsStore.confirm(0, `Your search returned an estimated result and may not be accurate. Upgrade to a paid subscription for immediate access to premium searches.`, {
          title: 'Notice',
          confirmText: 'Upgrade',
          cancelText: 'Use less accurate result',
          type: 'is-warning',
          onConfirm: () => {
            // Note: backend/user/current getter doesn't exist in current codebase
            router.replace({ name: 'Signin' })
          }
        })
      }

      mainStore.setDisplayAddress(formattedAddress)
      return latLng
    } catch (e) {
      // Handle various error types
      if (e.message.includes('free-search-limit')) {
        dialogsStore.confirm(0, `You've reached your free search limit this month. Upgrade to a paid subscription to continue searching for addresses immediately.`, {
          title: 'Notice',
          confirmText: 'Upgrade',
          cancelText: 'Wait until next month',
          type: 'is-warning',
          onConfirm: () => {
            router.replace({ name: 'Signin' })
          }
        })
      } else if (e.message.includes('paid-search-limit')) {
        dialogsStore.confirm(0, `You have reached your subscription's search limit. Upgrade for immediate access to additional searches.`, {
          title: 'Notice',
          confirmText: 'Upgrade',
          cancelText: 'Wait until next month',
          type: 'is-warning',
          onConfirm: () => {
            router.replace({ name: 'Signin' })
          }
        })
      } else {
        dialogsStore.alert(1, e.message, { type: 'is-danger' })
      }
      throw e
    }
  }

  /**
   * Update address marker position
   * @param {Object} latLng - Coordinates { lat, lng } or Google Maps LatLng
   */
  const updateAddressMarker = (latLng) => {
    if (!latLng || (latLng.lat === undefined && typeof latLng.lat !== 'function') || (latLng.lng === undefined && typeof latLng.lng !== 'function')) {
      console.error('updateAddressMarker: invalid latLng', latLng)
      return
    }

    if (!map.value) {
      console.warn('updateAddressMarker: map not initialized')
      return
    }

    // Extract lat/lng values (handle both plain objects and Google Maps LatLng objects)
    const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat
    const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng

    // Create a Google Maps LatLng object
    const position = new google.maps.LatLng(lat, lng)

    if (!addressMarker.value) {
      addressMarker.value = new google.maps.Marker({
        position: position,
        label: addressMarkerLabel,
        draggable: route.name === 'ConfirmAddress'
      })

      addressMarker.value.addListener('dragend', (event) => {
        mainStore.setMapCenter(event.latLng)
      })
    }

    addressMarker.value.setOptions({
      position: position
    })

    addressMarker.value.setMap(map.value)
    map.value.setCenter(position)
  }

  /**
   * Set address marker visibility
   * @param {boolean} visible
   */
  const setMarkerVisible = (visible = true) => {
    if (addressMarker.value) {
      addressMarker.value.setMap(visible ? map.value : null)
    }

    if (!visible) {
      return
    }

    const mapCenter = mainStore.mapCenter
    if (map.value && !addressMarker.value && mapCenter && mapCenter.lat && mapCenter.lng) {
      updateAddressMarker({ lat: mapCenter.lat, lng: mapCenter.lng })
    }
  }

  /**
   * Set address marker draggable state
   * @param {boolean} draggable
   */
  const setMarkerDraggable = (draggable = true) => {
    if (addressMarker.value) {
      addressMarker.value.setOptions({ draggable })
    }
  }

  /**
   * Geocode address and update marker
   * @param {string} address
   */
  const updateAddress = (address) => {
    console.log('updateAddress called with:', address)
    geocodeAddress(address).then((latLng) => {
      console.log('geocodeAddress resolved with latLng:', latLng)
      updateAddressMarker(latLng)
    }, (result) => {
      console.log(result)
    })
  }

  /**
   * Watch address changes and trigger geocoding if needed
   */
  watch(
    () => mainStore.address,
    (newAddress) => {
      if (newAddress) {
        const mapCenter = mainStore.mapCenter
        if (!mapCenter || !mapCenter.lat || !mapCenter.lng) {
          console.log('Calling updateAddress with:', newAddress)
          updateAddress(newAddress)
        } else {
          console.log('Skipping updateAddress - mapCenter already exists:', mapCenter)
        }
      }
    }
  )

  /**
   * Watch route changes to control marker visibility/draggability
   */
  watch(
    () => route.name,
    (newRouteName) => {
      const isConfirmAddress = newRouteName === 'ConfirmAddress'
      setMarkerVisible(isConfirmAddress)
      setMarkerDraggable(isConfirmAddress)
    },
    { immediate: true }
  )

  return {
    addressMarker,
    geocodeAddress,
    updateAddressMarker,
    setMarkerVisible,
    setMarkerDraggable,
    updateAddress
  }
}
